import { useState } from "react";
import { Typography, Modal, Button, MultiEmailInput, Loading } from "@/components/atoms";
import { useBulkInviteDriverMutation } from "@/redux/apis/driver/driverApi";
import { BulkInviteDriverError } from "@/redux/apis/driver/types";
import { addToast } from "@/utils";

/** Pull the per-email failures out of the 422 ApiProblem body (all-invites-failed). */
const getInviteErrors = (error: unknown): BulkInviteDriverError[] => {
  const data = (error as { data?: { errors?: BulkInviteDriverError[] } })?.data;
  return Array.isArray(data?.errors) ? data!.errors : [];
};

interface InviteDriversModalProps {
  open: boolean;
  onClose: () => void;
}

export const InviteDriversModal = ({ open, onClose }: InviteDriversModalProps) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [bulkInviteDriver, { isLoading }] = useBulkInviteDriverMutation();

  const handleInvite = async () => {
    if (emails.length === 0) {
      addToast("info", "Please enter at least one email address");
      return;
    }

    try {
      const { success_count, errors = [] } = await bulkInviteDriver({ emails }).unwrap();

      addToast("success", `Invitation sent to ${success_count} driver(s)`);
      // Partial success: some emails were skipped (e.g. account already exists).
      errors.forEach(({ email, error }) => addToast("danger", `${email}: ${error}`));

      setEmails([]);
      onClose();
    } catch (error) {
      // All invites failed (422) — surface the specific reason per email.
      const inviteErrors = getInviteErrors(error);
      if (inviteErrors.length) {
        inviteErrors.forEach(({ email, error: message }) => addToast("danger", `${email}: ${message}`));
      } else {
        addToast("danger", "Failed to send invitations. Please try again.");
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} className="relative w-[85vw] md:w-[550px] lg:w-[600px]">
      {isLoading && <Loading />}
      <div className="flex flex-col">
        <div className="flex flex-col gap-y-2">
          <Typography variant="title">Invite Driver</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Send an invite for the driver to download the app and set up their account
          </Typography>
        </div>

        <div className="mt-5 rounded-lg border border-info bg-info/10 p-4">
          <Typography variant="paragraph" className="text-info-dark">
            Inviting a driver <span className="font-semibold">bypasses application rules</span>, allowing drivers invited through this flow
            to skip the standard application process and continue directly with setting up their account.
          </Typography>
        </div>

        <div className="mt-5 flex flex-col gap-y-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Driver Email(s)
          </Typography>
          <MultiEmailInput emails={emails} onChange={setEmails} />
          <Typography variant="paragraph" className="text-neutral-silver-gray">
            You can enter multiple email addresses separated by commas
          </Typography>
        </div>

        <div className="mt-10 flex justify-end gap-x-3">
          <Button variant="secondary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="lg" onClick={handleInvite} disabled={emails.length === 0 || isLoading}>
            Invite Driver(s)
          </Button>
        </div>
      </div>
    </Modal>
  );
};
